import { useState, useEffect } from 'react'
import Layout from '../../../../components/vision/Layout'
import ProtectedRoute from '../../../../components/vision/ProtectedRoute'
import { BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import FunnelChart from '../../../../components/vision/charts/FunnelChart'

export default function DashboardKPIsPage() {
  const [clientes, setClientes] = useState<any[]>([])
  const [selectedCliente, setSelectedCliente] = useState<any>(null)
  const [competencia1, setCompetencia1] = useState<string>('')
  const [competencia2, setCompetencia2] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [dadosDashboard, setDadosDashboard] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showClientesList, setShowClientesList] = useState(false)
  const [modoVisualizacao, setModoVisualizacao] = useState<'comparacao' | 'periodo'>('comparacao')
  const [mesInicio, setMesInicio] = useState<string>('')
  const [mesFim, setMesFim] = useState<string>('')
  const [dadosPeriodo, setDadosPeriodo] = useState<any>(null)
  const [dadosFunil1, setDadosFunil1] = useState<any>(null)
  const [dadosFunil2, setDadosFunil2] = useState<any>(null)
  const [loadingFunil, setLoadingFunil] = useState(false)
  const [funilAtivo, setFunilAtivo] = useState<1 | 2>(1)
  const [modoGranularidade, setModoGranularidade] = useState<'mensal' | 'semanal'>('mensal')
  const [mesAnalise, setMesAnalise] = useState<string>('')
  const [dadosSemanais, setDadosSemanais] = useState<any>(null)

  useEffect(() => {
    fetchClientes()
    // Definir competência 1 como mês atual
    const hoje = new Date()
    const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`
    setCompetencia1(mesAtual)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.cliente-search-container')) {
        setShowClientesList(false)
      }
    }

    if (showClientesList) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showClientesList])

  const fetchClientes = async () => {
    try {
      const res = await fetch('/api/gestao/clientes')
      if (res.ok) {
        const response = await res.json()
        const clientesData = response.data || response
        setClientes(Array.isArray(clientesData) ? clientesData : [])
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error)
      setClientes([])
    }
    setLoading(false)
  }

  const fetchDadosFunil = async (clienteId: string, comp1: string, comp2?: string) => {
    setLoadingFunil(true)
    setFunilAtivo(1)
    try {
      // Buscar funil da competência 1
      const params1 = new URLSearchParams({
        cliente_id: clienteId,
        mes_competencia: comp1
      })
      const res1 = await fetch(`/api/gestao/kpis/funil?${params1}`)
      
      if (res1.ok) {
        const dados1 = await res1.json()
        setDadosFunil1(dados1)
      } else {
        console.error('Erro ao buscar funil 1:', res1.status)
        setDadosFunil1(null)
      }

      // Buscar funil da competência 2 (se existir)
      if (comp2) {
        const params2 = new URLSearchParams({
          cliente_id: clienteId,
          mes_competencia: comp2
        })
        const res2 = await fetch(`/api/gestao/kpis/funil?${params2}`)
        
        if (res2.ok) {
          const dados2 = await res2.json()
          setDadosFunil2(dados2)
        } else {
          console.error('Erro ao buscar funil 2:', res2.status)
          setDadosFunil2(null)
        }
      } else {
        setDadosFunil2(null)
      }
    } catch (error) {
      console.error('Erro ao buscar dados do funil:', error)
      setDadosFunil1(null)
      setDadosFunil2(null)
    } finally {
      setLoadingFunil(false)
    }
  }

  const fetchDadosDashboard = async (clienteId: string, comp1: string, comp2: string) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        cliente_id: clienteId,
        competencia1: comp1
      })
      if (comp2) params.append('competencia2', comp2)

      const res = await fetch(`/api/gestao/kpis/dashboard-resultados?${params}`)
      if (res.ok) {
        const dados = await res.json()
        setDadosDashboard(dados)
      }
    } catch (error) {
      console.error('Erro ao buscar dashboard:', error)
    }
    setLoading(false)
  }

  const fetchDadosPeriodo = async (clienteId: string, inicio: string, fim: string) => {
    console.log('fetchDadosPeriodo chamado:', { clienteId, inicio, fim })
    setLoading(true)
    setDadosPeriodo(null)
    try {
      const params = new URLSearchParams({
        cliente_id: clienteId,
        mes_inicio: inicio,
        mes_fim: fim
      })

      console.log('Chamando API:', `/api/gestao/kpis/dashboard-periodo?${params}`)
      const res = await fetch(`/api/gestao/kpis/dashboard-periodo?${params}`)
      console.log('Resposta da API:', res.status, res.ok)
      
      const responseText = await res.text()
      console.log('Resposta raw:', responseText)
      
      try {
        const dados = JSON.parse(responseText)
        console.log('Dados parseados:', dados)
        
        if (res.ok) {
          setDadosPeriodo(dados)
        } else {
          console.error('Erro na API:', dados)
        }
      } catch (parseError) {
        console.error('Erro ao fazer parse do JSON:', parseError)
        console.error('Texto recebido:', responseText)
      }
    } catch (error) {
      console.error('Erro ao buscar dados do período:', error)
    }
    setLoading(false)
  }

  const fetchDadosSemanais = async (clienteId: string, mesCompetencia: string) => {
    console.log('fetchDadosSemanais chamado:', { clienteId, mesCompetencia })
    setLoading(true)
    setDadosSemanais(null)
    try {
      const params = new URLSearchParams({
        cliente_id: clienteId,
        mes_competencia: mesCompetencia
      })

      console.log('Chamando API:', `/api/gestao/kpis/dashboard-semanal?${params}`)
      const res = await fetch(`/api/gestao/kpis/dashboard-semanal?${params}`)
      console.log('Resposta da API:', res.status, res.ok)
      
      if (res.ok) {
        const dados = await res.json()
        console.log('Dados semanais:', dados)
        setDadosSemanais(dados)
      } else {
        console.error('Erro ao buscar dados semanais:', res.status)
      }
    } catch (error) {
      console.error('Erro ao buscar dados semanais:', error)
    }
    setLoading(false)
  }

  const handleSelectCliente = async (cliente: any) => {
    setSelectedCliente(cliente)
    setShowClientesList(false)
    setSearchTerm('')
    setLoading(true)
    setDadosDashboard(null)
    setDadosPeriodo(null)
    
    if (modoVisualizacao === 'comparacao') {
      await fetchDadosDashboard(cliente.id, competencia1, competencia2)
    } else if (modoVisualizacao === 'periodo' && mesInicio && mesFim) {
      await fetchDadosPeriodo(cliente.id, mesInicio, mesFim)
    }
    
    setLoading(false)
  }

  useEffect(() => {
    if (selectedCliente && competencia1 && modoVisualizacao === 'comparacao') {
      fetchDadosDashboard(selectedCliente.id, competencia1, competencia2)
      fetchDadosFunil(selectedCliente.id, competencia1, competencia2)
    }
  }, [competencia1, competencia2, modoVisualizacao])

  useEffect(() => {
    if (selectedCliente && mesInicio && mesFim && modoVisualizacao === 'periodo' && modoGranularidade === 'mensal') {
      fetchDadosPeriodo(selectedCliente.id, mesInicio, mesFim)
    }
  }, [mesInicio, mesFim, modoVisualizacao, modoGranularidade])

  useEffect(() => {
    if (selectedCliente && mesAnalise && modoVisualizacao === 'periodo' && modoGranularidade === 'semanal') {
      fetchDadosSemanais(selectedCliente.id, mesAnalise)
    }
  }, [mesAnalise, modoVisualizacao, modoGranularidade])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  }

  const formatInteger = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  const isIntegerIndicator = (nome: string) => {
    const indicadoresInteiros = [
      'Leads Gerados',
      'Leads Agendados',
      'Agendamento',
      'Comparecimento',
      'Total de Vendas',
      'Quantidade de Vendas'
    ]
    return indicadoresInteiros.some(ind => nome.toLowerCase().includes(ind.toLowerCase()))
  }

  const formatPercentual = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 2 
    }).format(value) + '%'
  }

  const getMelhorValorIndex = (indicador: any) => {
    const valores = indicador.valores
    
    // Lista de indicadores onde MENOR é melhor
    const menorEhMelhor = [
      'custo por lead',
      'custo por agendamento',
      'custo por comparecimento',
      'custo por fechamento',
      'cac',
      'investimento em mídias',
      'investimento em midias',
      'valor investido',
      'budget total'
    ]
    
    const nomeIndicador = indicador.nome.toLowerCase()
    const ehMenorMelhor = menorEhMelhor.some(termo => 
      nomeIndicador.includes(termo)
    )
    
    if (ehMenorMelhor) {
      // Encontrar menor valor (excluindo zeros)
      const valoresNaoZero = valores.filter((v: number) => v > 0)
      if (valoresNaoZero.length === 0) return -1
      const menorValor = Math.min(...valoresNaoZero)
      return valores.findIndex((v: number) => v === menorValor && v > 0)
    } else {
      // Encontrar maior valor
      const maiorValor = Math.max(...valores)
      if (maiorValor === 0) return -1
      return valores.findIndex((v: number) => v === maiorValor)
    }
  }

  const filteredClientes = clientes.filter((cliente: any) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      cliente.nome_empresa?.toLowerCase().includes(searchLower) ||
      cliente.razao_social?.toLowerCase().includes(searchLower) ||
      cliente.nome_completo?.toLowerCase().includes(searchLower)
    )
  })

  return (
    <ProtectedRoute>
      <Layout>
        <div className="p-8">
          <div className="mb-8">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-gray-900">Dashboard de KPIs</h1>
              <p className="text-gray-600 mt-2">Visualize e compare o desempenho dos seus clientes</p>
            </div>

            <div className="flex justify-center mb-6">
              <div className="relative cliente-search-container w-full max-w-2xl">
                <input
                  type="text"
                  placeholder="Buscar cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setShowClientesList(true)}
                  className="w-full pl-10 pr-10 py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                />
                <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {showClientesList && (
                  <div className="absolute top-full mt-2 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl max-h-96 overflow-y-auto z-50">
                    {filteredClientes.length > 0 ? (
                      <div className="py-2">
                        {filteredClientes.map((cliente: any) => (
                          <button
                            key={cliente.id}
                            onClick={() => handleSelectCliente(cliente)}
                            className="w-full text-left px-6 py-4 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-semibold text-gray-900 text-base">
                              {cliente.nome_empresa || cliente.razao_social}
                            </div>
                            {cliente.nome_completo && (
                              <div className="text-sm text-gray-500 mt-1">{cliente.nome_completo}</div>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="px-6 py-8 text-center text-gray-500">
                        Nenhum cliente encontrado
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {selectedCliente && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span className="text-lg font-bold text-blue-900">
                      {selectedCliente.nome_empresa || selectedCliente.razao_social}
                    </span>
                    <button
                      onClick={() => setSelectedCliente(null)}
                      className="ml-3 text-blue-600 hover:text-blue-800 transition-colors"
                      title="Remover seleção"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 mb-6">
                  <span className="text-sm font-semibold text-gray-700">Modo de Visualização:</span>
                  <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                    <button
                      onClick={() => setModoVisualizacao('comparacao')}
                      className={`px-4 py-2 rounded-md font-semibold transition-all ${
                        modoVisualizacao === 'comparacao'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-transparent text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      📊 Comparação
                    </button>
                    <button
                      onClick={() => setModoVisualizacao('periodo')}
                      className={`px-4 py-2 rounded-md font-semibold transition-all ${
                        modoVisualizacao === 'periodo'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-transparent text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      📈 Período
                    </button>
                  </div>
                </div>

                {modoVisualizacao === 'comparacao' ? (
                  <div className="flex items-center justify-center gap-6 mb-4">
                    <div className="relative">
                      <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 rounded-2xl border-2 border-blue-200 shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-center gap-2">
                          <div className="bg-blue-500 p-2 rounded-lg">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="flex flex-col">
                            <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Competência 1</label>
                            <input
                              type="month"
                              value={competencia1}
                              onChange={(e) => setCompetencia1(e.target.value)}
                              className="mt-1 px-3 py-1.5 bg-white border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none font-semibold text-gray-800 transition-all cursor-pointer min-w-[180px]"
                              style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    </div>

                    <div className="relative">
                      <div className="flex items-center gap-3 bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 rounded-2xl border-2 border-purple-200 shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-center gap-2">
                          <div className="bg-purple-500 p-2 rounded-lg">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="flex flex-col">
                            <label className="text-xs font-semibold text-purple-700 uppercase tracking-wide">vs Competência 2</label>
                            <input
                              type="month"
                              value={competencia2}
                              onChange={(e) => setCompetencia2(e.target.value)}
                              className="mt-1 px-3 py-1.5 bg-white border-2 border-purple-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none font-semibold text-gray-800 transition-all cursor-pointer min-w-[180px]"
                              style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}
                              placeholder="Opcional"
                            />
                          </div>
                          {competencia2 && (
                            <button
                              onClick={() => setCompetencia2('')}
                              className="ml-2 text-purple-400 hover:text-red-500 transition-colors"
                              title="Limpar comparação"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Toggle de Granularidade */}
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <span className="text-sm font-semibold text-gray-700">Granularidade:</span>
                      <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                        <button
                          onClick={() => setModoGranularidade('mensal')}
                          className={`px-4 py-2 rounded-md font-semibold transition-all ${
                            modoGranularidade === 'mensal'
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-transparent text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          📅 Mensal
                        </button>
                        <button
                          onClick={() => setModoGranularidade('semanal')}
                          className={`px-4 py-2 rounded-md font-semibold transition-all ${
                            modoGranularidade === 'semanal'
                              ? 'bg-orange-600 text-white shadow-md'
                              : 'bg-transparent text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          📊 Semanal
                        </button>
                      </div>
                    </div>

                    {/* Seletores de Data - Modo Mensal */}
                    {modoGranularidade === 'mensal' ? (
                      <div className="flex items-center justify-center gap-6 mb-4">
                        <div className="relative">
                          <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 rounded-2xl border-2 border-blue-200 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-2">
                              <div className="bg-blue-500 p-2 rounded-lg">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div className="flex flex-col">
                                <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Início</label>
                                <input
                                  type="month"
                                  value={mesInicio}
                                  onChange={(e) => setMesInicio(e.target.value)}
                                  className="mt-1 px-3 py-1.5 bg-white border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none font-semibold text-gray-800 transition-all cursor-pointer min-w-[180px]"
                                  style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </div>

                        <div className="relative">
                          <div className="flex items-center gap-3 bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 rounded-2xl border-2 border-purple-200 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-2">
                              <div className="bg-purple-500 p-2 rounded-lg">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div className="flex flex-col">
                                <label className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Fim</label>
                                <input
                                  type="month"
                                  value={mesFim}
                                  onChange={(e) => setMesFim(e.target.value)}
                                  min={mesInicio}
                                  className="mt-1 px-3 py-1.5 bg-white border-2 border-purple-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none font-semibold text-gray-800 transition-all cursor-pointer min-w-[180px]"
                                  style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Seletor de Mês - Modo Semanal */
                      <div className="flex items-center justify-center gap-6 mb-4">
                        <div className="relative">
                          <div className="flex items-center gap-3 bg-gradient-to-r from-orange-50 to-red-50 px-6 py-4 rounded-2xl border-2 border-orange-200 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-2">
                              <div className="bg-orange-500 p-2 rounded-lg">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div className="flex flex-col">
                                <label className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Mês para Análise Semanal</label>
                                <input
                                  type="month"
                                  value={mesAnalise}
                                  onChange={(e) => setMesAnalise(e.target.value)}
                                  className="mt-1 px-3 py-1.5 bg-white border-2 border-orange-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 focus:outline-none font-semibold text-gray-800 transition-all cursor-pointer min-w-[180px]"
                                  style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {selectedCliente ? (
            loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
              </div>
            ) : modoVisualizacao === 'comparacao' && dadosDashboard ? (
              <>
                {/* Tabela Detalhada de Indicadores */}
                {dadosDashboard.indicadores && dadosDashboard.indicadores.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Todos os Indicadores
                    </h2>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b-2 border-gray-200">
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Indicador</th>
                            <th className="text-right py-3 px-4 font-semibold text-gray-700">
                              {(() => {
                                const [ano, mes] = competencia1.split('-')
                                const data = new Date(parseInt(ano), parseInt(mes) - 1, 15)
                                return data.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })
                              })()}
                            </th>
                            {competencia2 && (
                              <>
                                <th className="text-right py-3 px-4 font-semibold text-gray-700">
                                  {(() => {
                                    const [ano, mes] = competencia2.split('-')
                                    const data = new Date(parseInt(ano), parseInt(mes) - 1, 15)
                                    return data.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })
                                  })()}
                                </th>
                                <th className="text-right py-3 px-4 font-semibold text-gray-700">Variação</th>
                              </>
                            )}
                            <th className="text-right py-3 px-4 font-semibold text-gray-700">Meta</th>
                            <th className="text-right py-3 px-4 font-semibold text-gray-700">% Atingido</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dadosDashboard.indicadores.map((ind: any, index: number) => (
                            <tr key={ind.id} className={`border-b border-gray-100 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                              <td className="py-3 px-4 font-medium text-gray-900">{ind.nome}</td>
                              <td className="py-3 px-4 text-right font-semibold text-gray-900">
                                {ind.tipo === 'monetario' 
                                  ? formatCurrency(ind.valor1) 
                                  : ind.tipo === 'percentual' 
                                    ? formatPercentual(ind.valor1)
                                    : isIntegerIndicator(ind.nome)
                                      ? formatInteger(ind.valor1)
                                      : formatNumber(ind.valor1)
                                }
                              </td>
                              {competencia2 && (
                                <>
                                  <td className="py-3 px-4 text-right text-gray-600">
                                    {ind.tipo === 'monetario' 
                                      ? formatCurrency(ind.valor2) 
                                      : ind.tipo === 'percentual' 
                                        ? formatPercentual(ind.valor2)
                                        : isIntegerIndicator(ind.nome)
                                          ? formatInteger(ind.valor2)
                                          : formatNumber(ind.valor2)
                                    }
                                  </td>
                                  <td className="py-3 px-4 text-right">
                                    <span className={`font-semibold ${parseFloat(ind.variacao) > 0 ? 'text-green-600' : parseFloat(ind.variacao) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                      {parseFloat(ind.variacao) > 0 ? '↑' : parseFloat(ind.variacao) < 0 ? '↓' : '='} {Math.abs(parseFloat(ind.variacao)).toFixed(1)}%
                                    </span>
                                  </td>
                                </>
                              )}
                              <td className="py-3 px-4 text-right text-gray-600">
                                {ind.valorMeta 
                                  ? (
                                    ind.tipo === 'monetario' 
                                      ? formatCurrency(ind.valorMeta) 
                                      : ind.tipo === 'percentual' 
                                        ? formatPercentual(ind.valorMeta)
                                        : isIntegerIndicator(ind.nome)
                                          ? formatInteger(ind.valorMeta)
                                          : formatNumber(ind.valorMeta)
                                    ) 
                                  : '-'
                                }
                              </td>
                              <td className="py-3 px-4 text-right">
                                {ind.percentualMeta ? (
                                  <span className={`font-semibold ${parseFloat(ind.percentualMeta) >= 100 ? 'text-green-600' : parseFloat(ind.percentualMeta) >= 75 ? 'text-yellow-600' : 'text-red-600'}`}>
                                    {parseFloat(ind.percentualMeta).toFixed(0)}%
                                  </span>
                                ) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Gráfico de Funil com Navegação */}
                {(dadosFunil1 || dadosFunil2) && !loadingFunil && (
                  <div className="mb-8">
                    {/* Controles de Navegação (apenas se houver 2 funis) */}
                    {dadosFunil1 && dadosFunil2 && (
                      <div className="flex items-center justify-between mb-6 bg-white rounded-xl shadow-lg p-4">
                        {/* Seta Esquerda */}
                        <button
                          onClick={() => setFunilAtivo(1)}
                          disabled={funilAtivo === 1}
                          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                            funilAtivo === 1
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-500 text-white hover:bg-blue-600 hover:scale-105'
                          }`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          <span>Período Anterior</span>
                        </button>

                        {/* Indicador Central */}
                        <div className="text-center">
                          <div className="text-xl font-bold text-gray-900">
                            {funilAtivo === 1 && dadosFunil1 && (() => {
                              const [ano, mes] = dadosFunil1.periodo.split('-')
                              const data = new Date(parseInt(ano), parseInt(mes) - 1, 15)
                              return data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase()
                            })()}
                            {funilAtivo === 2 && dadosFunil2 && (() => {
                              const [ano, mes] = dadosFunil2.periodo.split('-')
                              const data = new Date(parseInt(ano), parseInt(mes) - 1, 15)
                              return data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase()
                            })()}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            Período {funilAtivo} de 2
                          </div>
                        </div>

                        {/* Seta Direita */}
                        <button
                          onClick={() => setFunilAtivo(2)}
                          disabled={funilAtivo === 2}
                          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                            funilAtivo === 2
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-500 text-white hover:bg-blue-600 hover:scale-105'
                          }`}
                        >
                          <span>Próximo Período</span>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    )}

                    {/* Funil Ativo com Transição */}
                    <div className="transition-opacity duration-300">
                      {funilAtivo === 1 && dadosFunil1 && dadosFunil1.funil && (
                        <FunnelChart 
                          data={dadosFunil1.funil} 
                          periodo={`${dadosFunil1.cliente.nome} - ${(() => {
                            const [ano, mes] = dadosFunil1.periodo.split('-')
                            const data = new Date(parseInt(ano), parseInt(mes) - 1, 15)
                            return data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase()
                          })()}`}
                        />
                      )}
                      {funilAtivo === 2 && dadosFunil2 && dadosFunil2.funil && (
                        <FunnelChart 
                          data={dadosFunil2.funil} 
                          periodo={`${dadosFunil2.cliente.nome} - ${(() => {
                            const [ano, mes] = dadosFunil2.periodo.split('-')
                            const data = new Date(parseInt(ano), parseInt(mes) - 1, 15)
                            return data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase()
                          })()}`}
                        />
                      )}
                    </div>
                  </div>
                )}

                {loadingFunil && (
                  <div className="bg-white rounded-xl shadow-lg p-12 mb-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando funil de vendas...</p>
                  </div>
                )}

                {/* Gráfico de Evolução Temporal */}
                {dadosDashboard.evolucao && dadosDashboard.evolucao.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                      </svg>
                      Evolução nos Últimos Meses
                    </h2>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={dadosDashboard.evolucao}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mes" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                          formatter={(value: any) => formatNumber(value)}
                        />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="leads" 
                          stroke="#3b82f6" 
                          strokeWidth={3} 
                          name="Leads Gerados"
                          dot={{ fill: '#3b82f6', r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="vendas" 
                          stroke="#10b981" 
                          strokeWidth={3} 
                          name="Vendas"
                          dot={{ fill: '#10b981', r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="investimento" 
                          stroke="#f59e0b" 
                          strokeWidth={3} 
                          name="Investimento (R$)"
                          dot={{ fill: '#f59e0b', r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Gráfico de Comparação de Indicadores Base */}
                {competencia2 && dadosDashboard.indicadores && dadosDashboard.indicadores.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Comparação de Indicadores Base
                    </h2>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart 
                        data={dadosDashboard.indicadores.filter((ind: any) => ind.categoria === 'base').slice(0, 8)}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="nome" 
                          angle={-45} 
                          textAnchor="end" 
                          height={100}
                          interval={0}
                        />
                        <YAxis />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                          formatter={(value: any, name?: string) => {
                            const ind = dadosDashboard.indicadores.find((i: any) => i.nome === name)
                            if (ind?.tipo === 'monetario') return formatCurrency(value)
                            if (ind?.tipo === 'percentual') return formatPercentual(value)
                            return formatNumber(value)
                          }}
                        />
                        <Legend />
                        <Bar 
                          dataKey="valor1" 
                          fill="#3b82f6" 
                          name={new Date(competencia1 + '-01').toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}
                          radius={[8, 8, 0, 0]}
                        />
                        <Bar 
                          dataKey="valor2" 
                          fill="#f59e0b" 
                          name={new Date(competencia2 + '-01').toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            ) : modoVisualizacao === 'periodo' && dadosPeriodo ? (
              <>
                {/* Cards de Totalizadores do Período */}
                <div className="grid grid-cols-5 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium opacity-90">Total Leads</div>
                      <svg className="w-5 h-5 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold">{formatInteger(dadosPeriodo.totalizadores.totalLeads)}</div>
                    <div className="text-xs opacity-90 mt-1">{dadosPeriodo.totalizadores.numMeses} meses</div>
                  </div>

                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium opacity-90">Total Vendas</div>
                      <svg className="w-5 h-5 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold">{formatInteger(dadosPeriodo.totalizadores.totalVendas)}</div>
                    <div className="text-xs opacity-90 mt-1">{dadosPeriodo.totalizadores.numMeses} meses</div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium opacity-90">Investimento</div>
                      <svg className="w-5 h-5 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold">{formatCurrency(dadosPeriodo.totalizadores.totalInvestimento)}</div>
                    <div className="text-xs opacity-90 mt-1">Total do período</div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium opacity-90">Faturamento</div>
                      <svg className="w-5 h-5 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold">{formatCurrency(dadosPeriodo.totalizadores.totalFaturamento)}</div>
                    <div className="text-xs opacity-90 mt-1">Total do período</div>
                  </div>

                  <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium opacity-90">ROI Médio</div>
                      <svg className="w-5 h-5 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold">{dadosPeriodo.totalizadores.roiMedio.toFixed(1)}%</div>
                    <div className="text-xs opacity-90 mt-1">Média do período</div>
                  </div>
                </div>

                {/* Tabela Evolutiva Mensal */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Evolução Mensal - {dadosPeriodo.periodo.inicio} a {dadosPeriodo.periodo.fim}
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 sticky left-0 bg-white z-10 shadow-[2px_0_5px_rgba(0,0,0,0.1)] min-w-[200px] whitespace-nowrap">Indicador</th>
                          {dadosPeriodo.meses.map((mes: string) => {
                            const [ano, mesNum] = mes.split('-')
                            const data = new Date(parseInt(ano), parseInt(mesNum) - 1, 15)
                            return (
                              <th key={mes} className="text-right py-3 px-4 font-semibold text-gray-700 min-w-[100px]">
                                {data.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })}
                              </th>
                            )
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {dadosPeriodo.indicadores.map((ind: any, index: number) => {
                          const melhorIndex = getMelhorValorIndex(ind)
                          
                          return (
                            <tr key={ind.id} className={`border-b border-gray-100 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                              <td className="py-3 px-4 font-medium text-gray-900 sticky left-0 bg-inherit z-10 shadow-[2px_0_5px_rgba(0,0,0,0.1)] min-w-[200px] whitespace-nowrap">{ind.nome}</td>
                              {ind.valores.map((valor: number, i: number) => {
                                const isMelhor = i === melhorIndex && valor > 0
                                
                                return (
                                  <td 
                                    key={i} 
                                    className={`py-3 px-4 text-right ${
                                      isMelhor 
                                        ? 'text-green-600' 
                                        : 'text-gray-700'
                                    }`}
                                  >
                                    {ind.tipo === 'monetario' 
                                      ? formatCurrency(valor) 
                                      : ind.tipo === 'percentual' 
                                        ? formatPercentual(valor)
                                        : isIntegerIndicator(ind.nome)
                                          ? formatInteger(valor)
                                          : formatNumber(valor)
                                    }
                                  </td>
                                )
                              })}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Gráfico de Evolução Financeira */}
                {dadosPeriodo.evolucao && dadosPeriodo.evolucao.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Evolução Financeira
                    </h2>
                    <ResponsiveContainer width="100%" height={400}>
                      <AreaChart data={dadosPeriodo.evolucao}>
                        <defs>
                          <linearGradient id="colorFaturamento" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="colorInvestimento" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.5} vertical={false} />
                        <XAxis 
                          dataKey="mes"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1F2937',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '12px',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                          }}
                          labelStyle={{ 
                            color: '#FFFFFF',
                            fontWeight: 'bold',
                            marginBottom: '8px'
                          }}
                          itemStyle={{ 
                            color: '#E5E7EB',
                            padding: '4px 0'
                          }}
                          formatter={(value: any, name?: string) => {
                            if (name === 'Faturamento') return [`R$ ${formatNumber(value)}`, '💰 Faturamento']
                            if (name === 'Investimento') return [`R$ ${formatNumber(value)}`, '📊 Investimento']
                            return [formatNumber(value), name || '']
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconType="circle"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="faturamento" 
                          stroke="#10B981" 
                          strokeWidth={3}
                          fillOpacity={1} 
                          fill="url(#colorFaturamento)" 
                          name="Faturamento"
                          animationDuration={800}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="investimento" 
                          stroke="#EF4444" 
                          strokeWidth={3}
                          fillOpacity={1} 
                          fill="url(#colorInvestimento)" 
                          name="Investimento"
                          animationDuration={800}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Gráfico de Indicadores-Chave */}
                {dadosPeriodo.evolucao && dadosPeriodo.evolucao.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                      </svg>
                      Evolução de Indicadores-Chave
                    </h2>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={dadosPeriodo.evolucao}>
                        <defs>
                          <linearGradient id="colorLeadsArea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.5} vertical={false} />
                        <XAxis 
                          dataKey="mes"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                        />
                        <YAxis 
                          yAxisId="left"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                        />
                        <YAxis 
                          yAxisId="right" 
                          orientation="right"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          tickFormatter={(value) => `${value.toFixed(1)}%`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1F2937',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '12px',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                          }}
                          labelStyle={{ 
                            color: '#FFFFFF',
                            fontWeight: 'bold',
                            marginBottom: '8px'
                          }}
                          itemStyle={{ 
                            color: '#E5E7EB',
                            padding: '4px 0'
                          }}
                          formatter={(value: any, name?: string) => {
                            if (name === 'Leads Gerados') return [`${formatInteger(value)} leads`, '🔵 Leads Gerados']
                            if (name === 'Vendas') return [`${formatInteger(value)} vendas`, '🟢 Vendas']
                            if (name === 'ROI') return [`${Number(value).toFixed(2)}%`, '💰 ROI']
                            return [formatNumber(value), name || '']
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconType="circle"
                        />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="leads" 
                          stroke="#3B82F6" 
                          strokeWidth={3} 
                          name="Leads Gerados"
                          dot={{ fill: '#3B82F6', r: 5, strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 7 }}
                          animationDuration={800}
                        />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="vendas" 
                          stroke="#10B981" 
                          strokeWidth={3} 
                          name="Vendas"
                          dot={{ fill: '#10B981', r: 5, strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 7 }}
                          animationDuration={800}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="roi" 
                          stroke="#F59E0B" 
                          strokeWidth={3} 
                          name="ROI"
                          dot={{ fill: '#F59E0B', r: 5, strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 7 }}
                          animationDuration={800}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            ) : modoVisualizacao === 'periodo' && modoGranularidade === 'semanal' && dadosSemanais ? (
              dadosSemanais.tipo === 'aviso_lancamento_mensal' ? (
                // Aviso de lançamento mensal
                <div className="max-w-4xl mx-auto">
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl shadow-xl p-8">
                    <div className="flex items-start gap-4">
                      <div className="bg-amber-500 p-4 rounded-xl flex-shrink-0">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-amber-900 mb-3">
                          ⚠️ {dadosSemanais.mensagem}
                        </h3>
                        <p className="text-amber-800 mb-4 leading-relaxed">
                          {dadosSemanais.detalhes}
                        </p>
                        <div className="bg-white/60 rounded-lg p-4 mb-4">
                          <p className="text-sm font-semibold text-amber-900 mb-2">💡 Sugestão:</p>
                          <p className="text-sm text-amber-800">{dadosSemanais.sugestao}</p>
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => window.location.href = '/vision/kpis/resultados'}
                            className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Relançar Dados por Semana
                          </button>
                          <button
                            onClick={() => setModoGranularidade('mensal')}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Ver Análise Mensal
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Informações adicionais */}
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Por que isso acontece?
                    </h4>
                    <ul className="space-y-2 text-sm text-blue-800">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Este mês possui lançamentos que cobrem o período completo (dia 1 ao último dia do mês)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Lançamentos mensais não podem ser divididos automaticamente em semanas sem perder precisão</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Para análise semanal, é necessário que os dados sejam lançados especificamente por semana</span>
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
              <>
                {/* Banner: planejamento alterado, precisa recalcular */}
                {dadosSemanais.precisa_recalculo && (
                  <div className="mb-6 flex items-start gap-4 bg-amber-50 border-2 border-amber-400 rounded-2xl p-5 shadow-md">
                    <div className="bg-amber-400 p-2.5 rounded-xl flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-amber-900 text-base">Planejamento alterado — recálculo necessário</p>
                      <p className="text-amber-800 text-sm mt-1">
                        O planejamento deste mês foi criado ou atualizado recentemente. Os indicadores calculados (taxas, custos, ROI etc.) podem estar desatualizados.
                        <br />
                        <strong>Acesse KPIs → Indicadores Calculados e refaça os cálculos semana a semana</strong> para que o dashboard reflita as informações corretas.
                      </p>
                    </div>
                  </div>
                )}

                {/* Cards de Totalizadores Semanais */}
                <div className="grid grid-cols-5 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium opacity-90">Total Leads</div>
                      <svg className="w-5 h-5 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold">{formatInteger(dadosSemanais.totalizadores.totalLeads)}</div>
                    <div className="text-xs opacity-90 mt-1">Ø {formatInteger(dadosSemanais.totalizadores.mediaLeadsSemanal)}/sem</div>
                  </div>

                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium opacity-90">Total Vendas</div>
                      <svg className="w-5 h-5 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold">{formatInteger(dadosSemanais.totalizadores.totalVendas)}</div>
                    <div className="text-xs opacity-90 mt-1">Ø {formatNumber(dadosSemanais.totalizadores.mediaVendasSemanal)}/sem</div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium opacity-90">Investimento</div>
                      <svg className="w-5 h-5 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold">{formatCurrency(dadosSemanais.totalizadores.totalInvestimento)}</div>
                    <div className="text-xs opacity-90 mt-1">Total do mês</div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium opacity-90">Faturamento</div>
                      <svg className="w-5 h-5 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold">{formatCurrency(dadosSemanais.totalizadores.totalFaturamento)}</div>
                    <div className="text-xs opacity-90 mt-1">Total do mês</div>
                  </div>

                  <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium opacity-90">Semanas</div>
                      <svg className="w-5 h-5 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold">{dadosSemanais.totalizadores.numSemanas}</div>
                    <div className="text-xs opacity-90 mt-1">no mês</div>
                  </div>
                </div>

                {/* Card de Desempenho vs Meta */}
                {dadosSemanais.indicadores.some((ind: any) => ind.metaMensal > 0) && (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-2xl shadow-xl p-6 mb-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="bg-blue-600 p-4 rounded-xl">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-blue-900">🎯 Desempenho vs Meta</h3>
                        <p className="text-sm text-blue-700">Total realizado no período</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {dadosSemanais.indicadores
                        .filter((ind: any) => ind.metaMensal > 0 && ['Leads Gerados', 'Leads Agendados', 'Total de Vendas', 'Total Faturado', 'Investimento em Mídias'].some(nome => ind.nome.includes(nome)))
                        .map((ind: any) => {
                          const realizado = ind.totalRealizado
                          const percentual = ind.metaMensal > 0 ? (realizado / ind.metaMensal) * 100 : 0
                          const statusColor = percentual >= 100 ? 'green' : percentual >= 70 ? 'yellow' : 'red'
                          const statusBg = percentual >= 100 ? 'bg-green-100' : percentual >= 70 ? 'bg-yellow-100' : 'bg-red-100'
                          const statusText = percentual >= 100 ? 'text-green-700' : percentual >= 70 ? 'text-yellow-700' : 'text-red-700'
                          const statusEmoji = percentual >= 100 ? '🟢' : percentual >= 70 ? '🟡' : '🔴'
                          const faltam = ind.metaMensal - realizado
                          
                          return (
                            <div key={ind.id} className={`${statusBg} rounded-xl p-4 border-2 border-${statusColor}-300`}>
                              <div className="text-sm font-semibold text-gray-700 mb-2">{ind.nome}</div>
                              
                              {/* Barra de Progresso */}
                              <div className="mb-3">
                                <div className="flex justify-between text-xs text-gray-600 mb-1">
                                  <span>Realizado</span>
                                  <span>{percentual.toFixed(1)}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full transition-all duration-500 ${
                                      percentual >= 100 ? 'bg-green-500' :
                                      percentual >= 70 ? 'bg-yellow-500' :
                                      'bg-red-500'
                                    }`}
                                    style={{ width: `${Math.min(percentual, 100)}%` }}
                                  />
                                </div>
                              </div>
                              
                              {/* Valores */}
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Meta:</span>
                                  <span className="font-bold text-gray-900">
                                    {ind.tipo === 'monetario' ? formatCurrency(ind.metaMensal) : formatInteger(ind.metaMensal)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Realizado:</span>
                                  <span className={`font-bold ${statusText}`}>
                                    {ind.tipo === 'monetario' ? formatCurrency(realizado) : formatInteger(realizado)}
                                  </span>
                                </div>
                                <div className={`flex justify-between pt-2 border-t border-${statusColor}-300`}>
                                  <span className="font-semibold">{statusEmoji} Status:</span>
                                  <span className={`font-bold ${statusText}`}>
                                    {faltam > 0 ? `Faltam ${ind.tipo === 'monetario' ? formatCurrency(faltam) : formatInteger(faltam)}` : 'Meta atingida!'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )
                        })
                      }
                    </div>
                  </div>
                )}

                {/* Cards de Destaques */}
                {(dadosSemanais.totalizadores.melhorSemana || dadosSemanais.totalizadores.piorSemana) && (
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {dadosSemanais.totalizadores.melhorSemana && (
                      <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl shadow-lg p-6 border-2 border-emerald-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-emerald-500 p-3 rounded-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">🏆 Melhor Semana</div>
                            <div className="text-2xl font-bold text-emerald-900">Semana {dadosSemanais.totalizadores.melhorSemana.numero}</div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-emerald-700">Leads:</span>
                            <span className="text-lg font-bold text-emerald-900">{formatInteger(dadosSemanais.totalizadores.melhorSemana.leads)}</span>
                          </div>
                          <div className="text-xs text-emerald-600">
                            {new Date(dadosSemanais.totalizadores.melhorSemana.periodo.split(' a ')[0] + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} - {new Date(dadosSemanais.totalizadores.melhorSemana.periodo.split(' a ')[1] + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    )}

                    {dadosSemanais.totalizadores.piorSemana && (
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl shadow-lg p-6 border-2 border-amber-200">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-amber-500 p-3 rounded-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-amber-700 uppercase tracking-wide">⚠️ Pior Semana</div>
                            <div className="text-2xl font-bold text-amber-900">Semana {dadosSemanais.totalizadores.piorSemana.numero}</div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-amber-700">Leads:</span>
                            <span className="text-lg font-bold text-amber-900">{formatInteger(dadosSemanais.totalizadores.piorSemana.leads)}</span>
                          </div>
                          <div className="text-xs text-amber-600">
                            {new Date(dadosSemanais.totalizadores.piorSemana.periodo.split(' a ')[0] + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} - {new Date(dadosSemanais.totalizadores.piorSemana.periodo.split(' a ')[1] + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Aviso de indicadores calculados não disponíveis */}
                {!dadosSemanais.temDadosCalculados && (
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 mb-6 flex items-start gap-4">
                    <div className="bg-amber-400 p-2.5 rounded-lg flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-amber-900 mb-1">Indicadores calculados ainda não foram gerados para este mês</p>
                      <p className="text-sm text-amber-800 mb-3">Os indicadores de lançamento (leads, vendas, investimento) já estão disponíveis abaixo. Para ver também os indicadores calculados (ROI, CAC, taxas de conversão) por semana, acesse a página de Indicadores Calculados.</p>
                      <button
                        onClick={() => window.location.href = '/vision/kpis/indicadores-calculados'}
                        className="bg-amber-600 hover:bg-amber-700 text-white text-sm px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 w-fit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M12 7h.01M15 7h.01M9 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-4" />
                        </svg>
                        Ir para Indicadores Calculados
                      </button>
                    </div>
                  </div>
                )}

                {/* Tabela Evolutiva Semanal */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Evolução Semanal - {dadosSemanais.mes}
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 sticky left-0 bg-white z-10 shadow-[2px_0_5px_rgba(0,0,0,0.1)] min-w-[200px] whitespace-nowrap">Indicador</th>
                          {dadosSemanais.semanas.map((semana: any) => (
                            <th key={semana.numero} className="text-right py-3 px-4 font-semibold text-gray-700 min-w-[120px]">
                              <div>S{semana.numero}</div>
                              <div className="text-xs font-normal text-gray-500">
                                {new Date(semana.inicio + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}-{new Date(semana.fim + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                              </div>
                            </th>
                          ))}
                          <th className="text-right py-3 px-4 font-semibold text-gray-700 bg-gray-50 min-w-[100px]">Total</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-700 bg-blue-50 min-w-[100px]">Meta</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-700 bg-blue-50 min-w-[100px]">% Atingido</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-700 bg-gray-50 min-w-[100px]">Média</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dadosSemanais.indicadores.map((ind: any, index: number) => {
                          const valoresNumericos = ind.valores.filter((v: any) => v !== null) as number[]
                          const total = valoresNumericos.reduce((a: number, b: number) => a + b, 0)
                          const media = valoresNumericos.length > 0 ? total / valoresNumericos.length : 0
                          const maxValor = valoresNumericos.filter((v: number) => v > 0).length > 0
                            ? Math.max(...valoresNumericos.filter((v: number) => v > 0))
                            : 0

                          // Para indicadores calculados, Total e Média devem ser recalculados
                          // a partir da soma dos indicadores base, não somados por semana
                          const totaisBase: Record<string, number> = {}
                          dadosSemanais.indicadores.forEach((i: any) => {
                            if (i.categoria !== 'calculado') {
                              const vals = i.valores.filter((v: any) => v !== null) as number[]
                              totaisBase[i.nome.toLowerCase()] = vals.reduce((a: number, b: number) => a + b, 0)
                            }
                          })
                          const _inv = totaisBase['investimento em mídias'] ?? totaisBase['budget total'] ?? 0
                          const _leads = totaisBase['leads gerados'] ?? 0
                          const _ag = totaisBase['leads agendados'] ?? 0
                          const _comp = totaisBase['comparecimento'] ?? 0
                          const _vendas = totaisBase['total de vendas'] ?? 0
                          const _fat = totaisBase['total faturado'] ?? 0
                          const nomeInd = ind.nome.toLowerCase()
                          let derivado: number | null = null
                          if (ind.categoria === 'calculado') {
                            if (nomeInd === 'custo por lead') derivado = _leads > 0 ? _inv / _leads : null
                            else if (nomeInd === 'custo por agendamento') derivado = _ag > 0 ? _inv / _ag : null
                            else if (nomeInd === 'custo por comparecimento') derivado = _comp > 0 ? _inv / _comp : null
                            else if (nomeInd === 'taxa lead→agendado') derivado = _leads > 0 ? (_ag / _leads) * 100 : null
                            else if (nomeInd === 'taxa agendado→comparecimento') derivado = _ag > 0 ? (_comp / _ag) * 100 : null
                            else if (nomeInd === 'taxa comparecimento→venda') derivado = _comp > 0 ? (_vendas / _comp) * 100 : null
                            else if (nomeInd === 'taxa conversão total') derivado = _leads > 0 ? (_vendas / _leads) * 100 : null
                            else if (nomeInd === 'roi') derivado = _inv > 0 ? _fat / _inv : null
                            else if (nomeInd === 'cac') derivado = _vendas > 0 ? _inv / _vendas : null
                            else if (nomeInd === 'ticket médio') derivado = _vendas > 0 ? _fat / _vendas : null
                            else if (nomeInd === 'margem de lucro') derivado = _fat > 0 ? ((_fat - _inv) / _fat) * 100 : null
                          }
                          const totalExibido = derivado !== null ? derivado : total
                          const mediaExibida = ind.categoria === 'calculado' ? (derivado ?? media) : media
                          const percentualAtingidoReal = ind.metaMensal > 0 ? (totalExibido / ind.metaMensal) * 100 : 0
                          
                          return (
                            <tr key={ind.id} className={`border-b border-gray-100 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                              <td className="py-3 px-4 font-medium text-gray-900 sticky left-0 bg-inherit z-10 shadow-[2px_0_5px_rgba(0,0,0,0.1)] min-w-[200px] whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  {ind.nome}
                                  {ind.categoria === 'calculado' && (
                                    <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">calc</span>
                                  )}
                                </div>
                              </td>
                              {ind.valores.map((valor: number | null, i: number) => {
                                const status = ind.statusPorSemana ? ind.statusPorSemana[i] : 'ok'
                                const isMelhor = valor !== null && valor === maxValor && valor > 0
                                const somaAcumulada = ind.valores.slice(0, i + 1).reduce((acc: number, v: number | null) => acc + (v ?? 0), 0)
                                const percentualAtingido = ind.metaMensal > 0 ? (somaAcumulada / ind.metaMensal) * 100 : 0
                                const statusEmoji = percentualAtingido >= 100 ? '🟢' : percentualAtingido >= 70 ? '🟡' : '🔴'
                                
                                return (
                                  <td 
                                    key={i} 
                                    className={`py-3 px-4 text-right ${
                                      isMelhor 
                                        ? 'text-green-600 font-bold' 
                                        : 'text-gray-700'
                                    }`}
                                  >
                                    {valor === null ? (
                                      <span className="text-gray-400 text-sm" title={status === 'sem_calculo' ? 'Cálculo não realizado' : 'Dados insuficientes'}>
                                        {status === 'sem_calculo' ? '—' : '~'}
                                      </span>
                                    ) : (
                                      <>
                                        <div>
                                          {ind.tipo === 'monetario' 
                                            ? formatCurrency(valor) 
                                            : ind.tipo === 'percentual' 
                                              ? formatPercentual(valor)
                                              : isIntegerIndicator(ind.nome)
                                                ? formatInteger(valor)
                                                : formatNumber(valor)
                                          }
                                        </div>
                                        {ind.metaMensal > 0 && somaAcumulada > 0 && (
                                          <div className="text-xs mt-1">
                                            {statusEmoji} {percentualAtingido.toFixed(1)}%
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </td>
                                )
                              })}
                              <td className="py-3 px-4 text-right font-bold text-gray-900 bg-gray-50">
                                {valoresNumericos.length === 0 && derivado === null ? '—' : (
                                  ind.tipo === 'monetario' 
                                    ? formatCurrency(totalExibido) 
                                    : ind.tipo === 'percentual' 
                                      ? formatPercentual(totalExibido)
                                      : isIntegerIndicator(ind.nome)
                                        ? formatInteger(totalExibido)
                                        : formatNumber(totalExibido)
                                )}
                              </td>
                              <td className="py-3 px-4 text-right font-semibold text-blue-700 bg-blue-50">
                                {ind.metaMensal > 0 ? (
                                  ind.tipo === 'monetario' 
                                    ? formatCurrency(ind.metaMensal) 
                                    : ind.tipo === 'percentual' 
                                      ? formatPercentual(ind.metaMensal)
                                      : isIntegerIndicator(ind.nome)
                                        ? formatInteger(ind.metaMensal)
                                        : formatNumber(ind.metaMensal)
                                ) : '-'}
                              </td>
                              <td className={`py-3 px-4 text-right font-bold bg-blue-50 ${
                                percentualAtingidoReal >= 100 ? 'text-green-600' :
                                percentualAtingidoReal >= 70 ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                {ind.metaMensal > 0 ? (
                                  <div>
                                    <div>{percentualAtingidoReal.toFixed(1)}%</div>
                                    <div className="text-xs font-normal text-gray-600 mt-1">
                                      {percentualAtingidoReal >= 100 ? '🟢' : percentualAtingidoReal >= 70 ? '🟡' : '🔴'}
                                    </div>
                                  </div>
                                ) : '-'}
                              </td>
                              <td className="py-3 px-4 text-right font-semibold text-blue-600 bg-gray-50">
                                {valoresNumericos.length === 0 && derivado === null ? '—' : (
                                  ind.tipo === 'monetario' 
                                    ? formatCurrency(mediaExibida) 
                                    : ind.tipo === 'percentual' 
                                      ? formatPercentual(mediaExibida)
                                      : isIntegerIndicator(ind.nome)
                                        ? formatInteger(mediaExibida)
                                        : formatNumber(mediaExibida)
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Gráfico de Evolução Semanal */}
                {dadosSemanais.evolucao && dadosSemanais.evolucao.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                      </svg>
                      Evolução de Indicadores-Chave (Semanal)
                    </h2>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={dadosSemanais.evolucao}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.5} vertical={false} />
                        <XAxis 
                          dataKey="semana"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                        />
                        <YAxis 
                          yAxisId="left"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                        />
                        <YAxis 
                          yAxisId="right" 
                          orientation="right"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          tickFormatter={(value) => `${value.toFixed(1)}%`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1F2937',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '12px',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                          }}
                          labelStyle={{ 
                            color: '#FFFFFF',
                            fontWeight: 'bold',
                            marginBottom: '8px'
                          }}
                          itemStyle={{ 
                            color: '#E5E7EB',
                            padding: '4px 0'
                          }}
                          formatter={(value: any, name?: string) => {
                            if (name === 'Leads Gerados') return [`${formatInteger(value)} leads`, '🔵 Leads']
                            if (name === 'Vendas') return [`${formatInteger(value)} vendas`, '🟢 Vendas']
                            if (name === 'ROI') return [`${Number(value).toFixed(2)}%`, '💰 ROI']
                            return [formatNumber(value), name || '']
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconType="circle"
                        />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="leads" 
                          stroke="#3B82F6" 
                          strokeWidth={3} 
                          name="Leads Gerados"
                          dot={{ fill: '#3B82F6', r: 6, strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 8 }}
                        />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="vendas" 
                          stroke="#10B981" 
                          strokeWidth={3} 
                          name="Vendas"
                          dot={{ fill: '#10B981', r: 6, strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 8 }}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="roi" 
                          stroke="#F59E0B" 
                          strokeWidth={3} 
                          name="ROI"
                          dot={{ fill: '#F59E0B', r: 6, strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 8 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Gráfico de Barras Comparativo */}
                {dadosSemanais.evolucao && dadosSemanais.evolucao.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Investimento vs Faturamento (Semanal)
                    </h2>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={dadosSemanais.evolucao}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.5} vertical={false} />
                        <XAxis 
                          dataKey="semana"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1F2937',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '12px',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                          }}
                          formatter={(value: any, name?: string) => {
                            if (name === 'Faturamento') return [`R$ ${formatNumber(value)}`, '💰 Faturamento']
                            if (name === 'Investimento') return [`R$ ${formatNumber(value)}`, '📊 Investimento']
                            return [formatNumber(value), name || '']
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                        />
                        <Bar 
                          dataKey="investimento" 
                          fill="#EF4444" 
                          name="Investimento"
                          radius={[8, 8, 0, 0]}
                        />
                        <Bar 
                          dataKey="faturamento" 
                          fill="#10B981" 
                          name="Faturamento"
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
              )
            ) : (
              <div className="text-center py-12 text-gray-500">
                {modoVisualizacao === 'comparacao' 
                  ? 'Selecione as competências para visualizar os dados' 
                  : modoGranularidade === 'mensal'
                    ? 'Selecione o período (mês início e fim) para visualizar os dados'
                    : 'Selecione o mês para análise semanal'}
              </div>
            )
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <div className="text-gray-400 text-6xl mb-4">📊</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Selecione um Cliente</h2>
              <p className="text-gray-600">Escolha um cliente acima para visualizar seus KPIs e métricas de desempenho</p>
            </div>
          )}
        </div>
      </Layout>
    </ProtectedRoute>
  )
}
